import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import {
  getGetJobQueryOptions,
  saveGeneratedActors,
} from "../../api/generated/endpoints/generation/generation";
import type { AiActor, Job } from "../../api/generated/model";
import { refreshTeamStorage } from "../../api/team-storage";
import { isSettled } from "../jobs/status";
import { jobPageInterval } from "../jobs/refetch";
import { useStreamStatus } from "../stream-status";

/**
 * An actor "Add AI Actor" started (live's pending avatar, `isGeneration`): its job, and its end.
 * Talking Head's and Actor Swap's (G14), whose forms both offer "Add AI Actor".
 */
export interface Making {
  jobId: string;
  failed: boolean;
}

/**
 * Live's pending AI Actors (`useActorGenerationResult`): each job "Add AI Actor" started is read
 * as a job's page reads one. Once it has made its portrait, the portrait is kept as the team's
 * actor (`saveGeneratedActors`, its one image, live's `saveAvatarGenerationImage`) and handed
 * over with `onMade`; a job that ended without one, or a save refused, is `onFailed`, so its card
 * says so. Each job is handled once.
 */
export function useMakings(options: {
  teamId: string;
  brandId: string;
  makings: readonly Making[];
  onMade: (jobId: string, actor: AiActor) => void;
  onFailed: (jobId: string) => void;
}) {
  const { teamId, brandId, makings, onMade, onFailed } = options;
  const queryClient = useQueryClient();
  const stream = useStreamStatus();
  const waiting = makings.filter((each) => !each.failed).map((each) => each.jobId);
  const jobs = useQueries({
    queries: waiting.map((jobId) => ({
      ...getGetJobQueryOptions(teamId, jobId),
      refetchInterval: (query: { state: { data: Parameters<typeof jobPageInterval>[0] } }) =>
        jobPageInterval(query.state.data, stream),
    })),
  });
  const handled = useRef(new Set<string>());
  // The latest callbacks, so an effect run by a job's read hands its result to the form as it is.
  const told = useRef({ onMade, onFailed });
  useEffect(() => {
    told.current = { onMade, onFailed };
  });
  const ended = jobs
    .map((read) => read.data)
    .filter((job): job is Job => job !== undefined && isSettled(job.status));
  useEffect(() => {
    for (const job of ended) {
      if (handled.current.has(job.id)) continue;
      handled.current.add(job.id);
      const portrait = job.outputs.find((output) => output.status === "delivered");
      if (job.status !== "succeeded" || portrait === undefined) {
        told.current.onFailed(job.id);
        continue;
      }
      saveGeneratedActors(teamId, brandId, { job_id: job.id, output_indexes: [portrait.index] })
        .then((saved) => {
          // The actor's portrait counts in the team's storage.
          void refreshTeamStorage(queryClient, teamId);
          const actor = saved.items[0];
          if (actor === undefined) told.current.onFailed(job.id);
          else told.current.onMade(job.id, actor);
        })
        .catch(() => told.current.onFailed(job.id));
    }
  }, [ended, teamId, brandId, queryClient]);
}
